import React, { FC, ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import { useResizeWatcher } from 'hooks/useResizeWatcher/useResizeWatcher';
import { useHintRoot } from 'hooks/usePortalRoot/usePortalRoot';
import {
    HintBox,
    HintButton,
    HintButtonsContainer,
    HintContainer,
    HintOverlayBottom,
    HintOverlayLeft,
    HintOverlayRight,
    HintOverlayTop,
    HintText,
    HintWrapper,
    Pointer,
} from './HintModal.css';

type HintModalPropTypes = {
    modalText: string;
    attachTo: HTMLElement;
    position?: 'top left' | 'top right' | 'bottom left' | 'bottom right';
    visible: boolean;
    closeHint: () => void;
    buttons?: Array<{
        text: string;
        onClick: () => void;
    }>;
    verticalOffset?: { desktop: number; tablet: number; mobile: number };
    horizontalOffset?: { desktop: number; tablet: number; mobile: number };
};

type CoordinatesPropTypes = {
    top: number;
    left: number;
    width: number;
    height: number;
};

const calculateCoords = (attachTo: HTMLElement) => {
    if (!attachTo) {
        return { top: 0, left: 0, width: 0, height: 0 };
    }
    const { height, width, top, left } = attachTo.getBoundingClientRect();

    return {
        top,
        left,
        width,
        height,
    };
};

export const HintModalComponent: FC<HintModalPropTypes> = ({
    modalText,
    attachTo,
    position = 'top left',
    verticalOffset = { desktop: 0, tablet: 0, mobile: 0 },
    horizontalOffset = { desktop: 0, tablet: 0, mobile: 0 },
    buttons,
    visible,
    closeHint,
}) => {
    const hintRoot = useHintRoot();
    const hintTextId = useId();
    const [coords, setCoords] = useState<CoordinatesPropTypes>({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    });

    const resizeHandler = useCallback(() => {
        setCoords(calculateCoords(attachTo));
    }, [setCoords]);

    useResizeWatcher(null, resizeHandler);

    const { top, left, width, height } = coords;

    useEffect(() => {
        setCoords(calculateCoords(attachTo));

        const onScroll = () => {
            setCoords(calculateCoords(attachTo));
        };

        document.addEventListener('scroll', onScroll);

        return () => {
            document.removeEventListener('scroll', onScroll);
        };
    }, [attachTo]);

    const hintButtons: ReactNode[] = buttons
        ? buttons.map((button, index) => (
              <HintButton
                  key={`hint-button-${index}`}
                  onClick={button.onClick}
                  isPrimary={index === 1}
                  label={button.text}
                  link={null}
                  data-event-name={'click'}
                  data-track-id={'button_hint'}
                  data-button-title={button.text}
              />
          ))
        : null;

    return (
        <ModalDialog
            as={HintWrapper}
            open={visible}
            container={hintRoot}
            onClose={closeHint}
            visible={visible}
            onClick={closeHint}
            aria-labelledby={hintTextId}
        >
            <HintContainer top={top} left={left} width={width} height={height}>
                <HintOverlayTop />
                <HintOverlayBottom />
                <HintOverlayLeft />
                <HintOverlayRight />
                <HintBox position={position.split(' ')} verticalOffset={verticalOffset} horizontalOffset={horizontalOffset}>
                    <HintText id={hintTextId}>{modalText}</HintText>
                    {hintButtons?.length > 0 && <HintButtonsContainer>{hintButtons}</HintButtonsContainer>}
                    <Pointer position={position.split(' ')} />
                </HintBox>
            </HintContainer>
        </ModalDialog>
    );
};
